import { Dayjs } from 'dayjs'

import { AstraParagraph, AstraTooltip, astraTwMerge, getOnlyDate, RowType } from '~/astra'
import { StatusCode } from '~/store/slices/filterSlice'
import { IdDate, useActivePractitioners, usePractitionerScheduleStatuses } from '~/storm'
import { getHolidayStyles } from '~/utils/utils'

import { getFridayStyles } from '../styles'

type Props = {
    name: string
    shortName?: string
    statuses: StatusCode[]
}

type CellProps = {
    date: Dayjs
    statuses: StatusCode[]
    practitionerIds: number[]
}

function DateStatusesCountsCell({ date, statuses, practitionerIds }: CellProps) {
    const practitionerStatuses = usePractitionerScheduleStatuses({
        where: {
            scheduled_date: getOnlyDate(date),
            status_code: { in: statuses },
            practitioner_id: { in: practitionerIds },
        },
        include: { practitioner: true },
    })

    if (practitionerStatuses.length === 0) {
        return null
    }

    const tooltip = practitionerStatuses
        .map(status => status.practitioner?.short_name)
        .filter(Boolean)
        .join(', ')

    return (
        <AstraTooltip tooltipContent={tooltip}>
            <AstraParagraph variant="body-small">{practitionerStatuses.length}</AstraParagraph>
        </AstraTooltip>
    )
}

export function useDateStatusesCountsRow({ name, shortName, statuses }: Props): RowType<IdDate> {
    const activePractitioners = useActivePractitioners()
    const practitionerIds = activePractitioners.map(p => p.id)

    return {
        key: name,
        rowHeader: () => (
            <AstraTooltip tooltipContent={name}>
                <AstraParagraph variant="body-small" weight="medium" className="cursor-pointer truncate px-2 py-1">
                    {shortName ?? name}
                </AstraParagraph>
            </AstraTooltip>
        ),
        cellClassName: ({ date }) => astraTwMerge('p-1 text-center', getFridayStyles(date), getHolidayStyles(date)),
        cellRender: ({ date }) => <DateStatusesCountsCell date={date} statuses={statuses} practitionerIds={practitionerIds} />,
    }
}
